import type { TeamWithMembers } from '../../../types/team';

/**
 * Team hierarchy helpers. Teams nest one level deep: a PARENT team (a head and
 * sub-teams, no agents of its own) and the SUB-TEAMS under it. A team with
 * neither a parent nor sub-teams is a standalone team, as before.
 */

/** A parent team is one that has sub-teams. */
export function isParentTeam(team: Pick<TeamWithMembers, 'subTeams'>): boolean {
  return (team.subTeams?.length ?? 0) > 0;
}

export function isSubTeam(team: Pick<TeamWithMembers, 'parentTeamId'>): boolean {
  return !!team.parentTeamId;
}

export interface TeamTree {
  /**
   * Teams to show at the top level: every team without a parent, plus any
   * sub-team whose parent is not in the list (e.g. a search matched only the
   * sub-team), so nothing the server returned is hidden.
   */
  roots: TeamWithMembers[];
  /** Sub-teams in the list, keyed by their parent's id. */
  childrenOf: Record<string, TeamWithMembers[]>;
}

/** Arrange a flat team list into parents with their sub-teams. */
export function buildTeamTree(teams: TeamWithMembers[]): TeamTree {
  const ids = new Set(teams.map((t) => t.id));
  const roots: TeamWithMembers[] = [];
  const childrenOf: Record<string, TeamWithMembers[]> = {};

  for (const team of teams) {
    if (team.parentTeamId && ids.has(team.parentTeamId)) {
      (childrenOf[team.parentTeamId] ??= []).push(team);
    } else {
      roots.push(team);
    }
  }
  for (const list of Object.values(childrenOf)) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return { roots, childrenOf };
}

/** Agents across all of a parent's sub-teams (an agent in two sub-teams counts twice). */
export function subTeamAgentTotal(team: Pick<TeamWithMembers, 'subTeams'>): number {
  return (team.subTeams ?? []).reduce((sum, st) => sum + st.agentCount, 0);
}

/**
 * Label for a team in a picker, so the hierarchy is visible where teams are
 * chosen: "Support › Billing" for a sub-team, and a parent is marked as a
 * queue because a ticket routed to it waits there for its head to triage.
 */
export function teamOptionLabel(
  team: Pick<TeamWithMembers, 'name' | 'designation' | 'parentName' | 'subTeams'>,
): string {
  if (isParentTeam(team)) return `${team.name} (queue — head assigns to a sub-team)`;
  const name = team.parentName ? `${team.parentName} › ${team.name}` : team.name;
  return team.designation ? `${name} — ${team.designation}` : name;
}

/**
 * Teams ordered for a picker: each parent directly followed by its sub-teams,
 * standalone teams in between, all by name.
 */
export function orderTeamsForPicker(teams: TeamWithMembers[]): TeamWithMembers[] {
  const { roots, childrenOf } = buildTeamTree(teams);
  return [...roots]
    .sort((a, b) => a.name.localeCompare(b.name))
    .flatMap((root) => [root, ...(childrenOf[root.id] ?? [])]);
}

/** Who is looking at the Teams screen — decides which moves are offered. */
export interface TeamViewer {
  userId?: string;
  /** Admins and owners can move any team; a parent's head only adds standalone teams. */
  isAdmin: boolean;
}

/**
 * Teams that can be added under `parent` as sub-teams. Never the parent
 * itself, a team already under it, or a team with sub-teams of its own (two
 * levels only). An admin also sees sub-teams of other parents (they move
 * across); anyone else sees only standalone teams.
 */
export function attachCandidates(
  teams: TeamWithMembers[],
  parent: TeamWithMembers,
  viewer: TeamViewer,
): TeamWithMembers[] {
  const parentIds = new Set(teams.map((t) => t.parentTeamId).filter((id): id is string => !!id));
  return teams
    .filter((t) => t.id !== parent.id && t.parentTeamId !== parent.id)
    .filter((t) => !isParentTeam(t) && !parentIds.has(t.id))
    .filter((t) => viewer.isAdmin || !t.parentTeamId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Whether `viewer` may add existing teams under `team`: admins, or the team's own head. */
export function canAddExistingTeams(team: TeamWithMembers, viewer: TeamViewer): boolean {
  if (team.parentTeamId) return false;
  return viewer.isAdmin || (!!viewer.userId && team.supervisorId === viewer.userId);
}

/**
 * Teams `team` could sit under, for the editor's "Parent team" dropdown: every
 * top-level team except itself. Empty for a team that has sub-teams, which
 * cannot become a sub-team.
 */
export function parentOptionsFor(
  teams: TeamWithMembers[],
  team: TeamWithMembers,
): TeamWithMembers[] {
  if (isParentTeam(team) || teams.some((t) => t.parentTeamId === team.id)) return [];
  return teams
    .filter((t) => t.id !== team.id && !t.parentTeamId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Teams a viewer may filter by: admins every team; anyone else the teams they
 * supervise or belong to, plus the sub-teams of a parent they head. Mirrors
 * the server's rule (the server still checks).
 */
export function teamsInViewerScope(
  teams: TeamWithMembers[],
  viewer: TeamViewer,
): TeamWithMembers[] {
  if (viewer.isAdmin) return teams;
  if (!viewer.userId) return [];
  const headOf = new Set(teams.filter((t) => t.supervisorId === viewer.userId).map((t) => t.id));
  return teams.filter(
    (t) =>
      t.supervisorId === viewer.userId ||
      (!!t.parentTeamId && headOf.has(t.parentTeamId)) ||
      (t.agents ?? []).some((a) => a.id === viewer.userId),
  );
}
